import React from "react";
import { Link } from "react-router-dom";
import { CaretUp, CaretDown, Sparkle } from "@phosphor-icons/react";
import { formatUSD, formatPct, formatSignedPct, formatEndDate } from "../lib/format";
import StarButton from "./StarButton";
import Sparkline from "./Sparkline";

function ProbBar({ yes, no }) {
  const y = Math.max(0, Math.min(1, yes ?? 0));
  const n = Math.max(0, Math.min(1, no ?? 1 - y));
  return (
    <div className="flex h-1.5 w-full overflow-hidden bg-white/5">
      <div className="bg-yes" style={{ width: `${y * 100}%` }} />
      <div className="bg-no" style={{ width: `${n * 100}%` }} />
    </div>
  );
}

export default function MarketCard({ market, edgeBadge }) {
  const change = Number(market.oneDayPriceChange || 0);
  const up = change >= 0;

  return (
    <div
      data-testid={`market-card-${market.id}`}
      className="group relative flex flex-col border border-white/10 bg-zinc-950/40 hover:border-white/30 hover:bg-zinc-900/60 transition-colors h-full"
    >
      <Link to={`/market/${market.id}`} className="flex flex-col h-full p-4">
        {edgeBadge != null && (
          <div className="absolute top-0 right-0 px-2 py-0.5 bg-edge text-black font-mono text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
            <Sparkle size={10} weight="fill" />
            AI Edge {formatSignedPct(edgeBadge, 0)}
          </div>
        )}

        <div className="flex items-start gap-3 mb-3">
          {market.image && (
            <img
              src={market.image}
              alt=""
              className="w-10 h-10 object-cover border border-white/10 grayscale group-hover:grayscale-0 transition-all"
              loading="lazy"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500 mb-1">
              {market.category || "Market"}
            </div>
            <h3 className="font-display text-sm font-medium leading-snug line-clamp-3 group-hover:text-white text-zinc-100">
              {market.question}
            </h3>
          </div>
        </div>

        <div className="mt-auto space-y-3">
          <div className="flex items-end justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase font-mono tracking-widest text-yes">Yes</div>
              <div className="font-mono text-2xl tabular-nums font-medium">
                {formatPct(market.yesPrice, 0)}
              </div>
            </div>
            <div className="flex-1 mx-3 self-end pb-1">
              <Sparkline marketId={market.id} height={26} />
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase font-mono tracking-widest text-no">No</div>
              <div className="font-mono text-2xl tabular-nums font-medium text-zinc-300">
                {formatPct(market.noPrice, 0)}
              </div>
            </div>
          </div>

          <ProbBar yes={market.yesPrice} no={market.noPrice} />

          <div className="flex items-center justify-between text-[11px] font-mono text-zinc-500 tabular-nums">
            <span>{formatUSD(market.volume)} vol</span>
            <span className={`inline-flex items-center gap-0.5 ${up ? "text-profit" : "text-loss"}`}>
              {up ? <CaretUp size={10} weight="fill" /> : <CaretDown size={10} weight="fill" />}
              {formatSignedPct(change, 1)}
            </span>
            <span>{formatEndDate(market.endDate)}</span>
          </div>
        </div>
      </Link>

      <div className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
        <StarButton market={market} />
      </div>
    </div>
  );
}
